import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";

import { LocalStorageConstants, OrganisationTypeConstants } from "../../../../constants";
import { parseProjectPhase, ProjectPhase } from "../../../../constants/project-phase";
import { ProjectDates, ProjectDatesData, SelectData } from "../../../../models";
import { IStateOrigin } from "../../../../navigators/navigateStateObjects";
import {
  GetTaskBoardResponse,
  searchProjectTracker,
  searchWorkflowTaskMilestones,
  SearchWorkflowTaskMilestonesResponse,
  SearchWorkflowTasksResponse,
} from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { useDeepLinkedTask, useTaskPanelWorkflow } from "../../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../../useAuth";
import { jsonReviver, safeLocalStorage } from "../../../../utils";
import { getProjectDetailsByUuid, getProjectRoadmapRoute } from "../../../../utils/routes";
import { dataGridMapFilterCriteria, GanttRoadmapRow, GanttTheme, Toast } from "../../../../widget";
import { toGanttRoadmapRows, toProjectPhaseFromPhaseBarId } from "./utils";

interface UseProjectRoadmapPageReturnData {
  errors: ServiceError[] | undefined;
  data: ProjectDatesData[] | undefined;
  ganttRows: GanttRoadmapRow[];
  theme: GanttTheme;
  onRowClick: (rowId: string) => void;
  onPhaseClick: (phaseId: string, rowId: string) => void;
  onMilestoneClick: (milestoneId: string, rowId: string) => void;
  onKeyDateClick: (keyDateId: string, rowId: string) => void;
  projectsMultiSelectData: SelectData | undefined;
  editDatesPanelData: ProjectDatesData | undefined;
  editDatesPanelPhase: ProjectPhase | undefined;
  setEditDatesPanelData: Dispatch<SetStateAction<ProjectDatesData | undefined>>;
  roadmapProjectUuids: string[];
  setRoadmapProjectUuids: Dispatch<SetStateAction<string[]>>;
  showAddProjectsModal: boolean;
  setShowAddProjectsModal: Dispatch<SetStateAction<boolean>>;
  closeSelectProjectsModal: () => void;
  showEditDatesPanel: boolean;
  closeEditDatesPanel: () => void;
  selectedTask: WorkflowTaskPanelNode | undefined;
  taskPanelWorkflow: GetTaskBoardResponse["results"][number] | undefined;
  showTaskPanel: boolean;
  taskPanelRefresh: boolean;
  handleTaskPanelRefresh: () => void;
  closeTaskPanel: () => void;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  onSaveProjectsSelectData: () => void;
  fetchSelectData: (displayName?: string) => Promise<void>;
}

type WorkflowTaskNode = SearchWorkflowTaskMilestonesResponse["results"][number];

/** The fuller task node the overlay edits — the milestone projection carries too little for it. */
type WorkflowTaskPanelNode = SearchWorkflowTasksResponse["results"][number];

/**
 * A milestone read's outcome, carried back rather than reported where it happens: the caller is the
 * only place that knows whether this read still owns the roadmap, and a superseded read must not warn
 * that milestones are missing when the read that replaced it has just drawn them.
 */
interface MilestoneRead {
  byProject: Map<string, WorkflowTaskNode[]>;
  notice?: "failed" | "truncated";
}

const MILESTONE_NOTICE_MESSAGE: Record<NonNullable<MilestoneRead["notice"]>, string> = {
  failed: "Unable to load the milestones for this roadmap, so none are shown.",
  truncated: "Some milestones are not shown on this roadmap.",
};

const MAX_PROJECTS = 15;
/**
 * One read covers every selected project. A selection capped at `MAX_PROJECTS` cannot plausibly reach
 * this ceiling, and the read reports it rather than truncating in silence if it ever does.
 */
const MAX_MILESTONES = 1000;

export const useProjectRoadmapPage = (): UseProjectRoadmapPageReturnData => {
  const { user, currentUserType } = useAuth();
  const projectRoadmapLocalStorageItemName = `${user?.userUuid ? `${user?.userUuid}_` : ""}${LocalStorageConstants.PROJECT_ROADMAP_PREFERENCES}`;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [errors, setErrors] = useState<ServiceError[]>();
  const [isLoading, setIsLoading] = useState(true);

  const [data, setData] = useState<ProjectDatesData[]>([]);
  const [projectsMultiSelectData, setProjectsMultiSelectData] = useState<SelectData>();

  const [roadmapProjectUuids, setRoadmapProjectUuids] = useState<string[]>([]);

  const [editDatesPanelData, setEditDatesPanelData] = useState<ProjectDatesData>();

  const [showEditDatesPanel, setShowEditDatesPanel] = useState(false);
  const [showAddProjectsModal, setShowAddProjectsModal] = useState(false);

  const editDatesPanelPhase = parseProjectPhase(searchParams.get("phase"));

  const closeEditDatesPanel = (): void => {
    setShowEditDatesPanel(false);
    navigate(getProjectRoadmapRoute(currentUserType), { replace: true });
  };

  const theme: GanttTheme = currentUserType === OrganisationTypeConstants.DEVELOPER ? "hub" : "seed";

  const ganttRows = useMemo(() => toGanttRoadmapRows(data), [data]);

  const onRowClick = useCallback(
    (rowId: string): void => {
      const route = getProjectDetailsByUuid(rowId, currentUserType);
      if (!route) return;

      navigate(route, {
        state: {
          origin: { goBackText: "Back to projects", from: getProjectRoadmapRoute(currentUserType) } as IStateOrigin,
        },
      });
    },
    [navigate, currentUserType]
  );

  const openEditDates = useCallback(
    (projectUuid: string, phase?: ProjectPhase): void => {
      const route = getProjectRoadmapRoute(currentUserType);
      if (!route) return;

      const params = new URLSearchParams({ projectUuid });
      if (phase) params.set("phase", phase);

      navigate(`${route}?${params.toString()}`);
    },
    [navigate, currentUserType]
  );

  const onPhaseClick = useCallback(
    (phaseId: string, rowId: string): void => openEditDates(rowId, toProjectPhaseFromPhaseBarId(phaseId)),
    [openEditDates]
  );

  /*
    A milestone diamond stands for a task, not for a project date field, so it opens that task's
    overlay rather than the edit-dates panel. The id the chart hands back is the task's uuid (see
    `toMilestones`), and it is passed on in `?taskUuid=` so the shared `useDeepLinkedTask` resolves
    and opens it — the same path a task notification already takes into a page (KAN-9609), rather
    than a second reader doing the same job. Phase bars and key dates keep the dates panel: those are
    project date fields with no task behind them.
  */
  const onMilestoneClick = useCallback(
    (milestoneId: string): void => {
      const route = getProjectRoadmapRoute(currentUserType);
      if (!route) return;

      navigate(`${route}?${new URLSearchParams({ taskUuid: milestoneId }).toString()}`);
    },
    [navigate, currentUserType]
  );

  const onKeyDateClick = useCallback(
    (_keyDateId: string, rowId: string): void => openEditDates(rowId),
    [openEditDates]
  );

  const updateProjectRoadmapLocalStorage = (): void => {
    try {
      safeLocalStorage.setItem(projectRoadmapLocalStorageItemName, JSON.stringify(roadmapProjectUuids));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err) {
      const message = err instanceof Error ? err.message : "An unknown error occurred while saving to local storage.";
      Toast.warn({ message });
    }
  };

  const fetchSelectData = useCallback(async (displayName?: string) => {
    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.dates = {
      operator: "neq",
      value: "{}",
    };

    filterCriteria.workflow = {
      uuid: {
        operator: "neq",
        value: null,
      },
    };

    if (displayName) {
      filterCriteria.displayName = {
        operator: "startsWith",
        value: displayName,
      };
    }

    const res = await searchProjectTracker({
      paging: {
        limit: 50,
        beforeCursor: null,
        afterCursor: null,
      },
      filter: { results: filterCriteria },
    });
    if (res.status !== Status.Success || res.data?.results !== undefined) {
      setProjectsMultiSelectData(
        res.data?.results.map((project) => ({
          key: project.uuid,
          value: project.displayName,
        })) ?? []
      );
    }
  }, []);

  /**
   * The selected projects' milestone tasks, grouped by the project each belongs to.
   *
   * Filtered and grouped on `parentObjectUuid` — the uniform parent object, which is set for a
   * general task as well as for one hanging off a workflow, so a single read covers both routes a
   * project's tasks arrive by. Neither of the alternatives covers the set: `workflowObjectUuid` is
   * only ever set on a workflow task, and `objectUuid` names the sub-object a task is *about*, so a
   * milestone related to an activity carries the activity there rather than the project.
   *
   * A failed read costs the diamonds, not the roadmap: the phase bars stand on their own, so the
   * milestones are reported missing and the rows are still drawn. What to report comes back as a
   * `notice` for the caller to raise once it knows this read has not been superseded.
   *
   * Ordered by deadline so that the ceiling, if it is ever reached, keeps the near end of the
   * timeline rather than an arbitrary slice of it.
   */
  const fetchMilestones = useCallback(async (uuids: string[]): Promise<MilestoneRead> => {
    const res = await searchWorkflowTaskMilestones({
      paging: {
        limit: MAX_MILESTONES,
        beforeCursor: null,
        afterCursor: null,
      },
      sort: [{ key: "results.deadlineDate", direction: "asc" }],
      filter: {
        results: {
          deadlineDate: { operator: "neq", value: null },
          isMilestone: { operator: "eq", value: true },
          parentObjectUuid: { operator: "in", value: uuids },
        },
      },
    }).catch(() => undefined);

    if (!res || res.status !== Status.Success || !res.data) {
      return { byProject: new Map(), notice: "failed" };
    }

    const byProject = res.data.results.reduce<Map<string, WorkflowTaskNode[]>>((grouped, task) => {
      // A task with no parent cannot be attributed, so it is dropped rather than defaulted onto a project.
      if (!task.parentObjectUuid) return grouped;

      return grouped.set(task.parentObjectUuid, [...(grouped.get(task.parentObjectUuid) ?? []), task]);
    }, new Map());

    /*
      Only the first page is read, so a selection past the ceiling has to say so: a diamond that is
      missing without a word reads as "this project has no milestones", which is the very thing the
      roadmap is drawn to show.
    */
    if (res.data.results.length < res.data.paging.total) {
      return { byProject, notice: "truncated" };
    }

    return { byProject };
  }, []);

  /**
   * Monotonic id of the newest roadmap read. Every read is tied to it so that a slower earlier
   * response cannot land on top of a later one — the reads fire from a mount, from saving the project
   * selection, and from saving a task in the overlay, none of which wait for the others.
   */
  const roadmapLoadIdRef = useRef(0);

  /**
   * `quiet` keeps the rows already on screen while re-reading them. The skeleton is right for a first
   * load, but blanking the whole chart to move one marker reads as though the roadmap were reloading
   * from scratch.
   */
  const fetchRoadmapData = useCallback(
    async (uuids: string[], { quiet = false }: { quiet?: boolean } = {}) => {
      roadmapLoadIdRef.current += 1;
      const loadId = roadmapLoadIdRef.current;
      const isCurrent = (): boolean => loadId === roadmapLoadIdRef.current;

      if (!quiet) setIsLoading(true);
      const projectTrackerFilterCriteria = dataGridMapFilterCriteria([]);

      projectTrackerFilterCriteria.uuid = {
        operator: "in",
        value: uuids,
      };

      projectTrackerFilterCriteria.workflow = {
        uuid: {
          operator: "neq",
          value: null,
        },
      };

      // Both reads are scoped by the same uuids, so neither waits on the other.
      const [res, milestones] = await Promise.all([
        searchProjectTracker({
          paging: {
            limit: MAX_PROJECTS,
            beforeCursor: null,
            afterCursor: null,
          },
          filter: { results: projectTrackerFilterCriteria },
        }),
        fetchMilestones(uuids),
      ]);

      /*
        A failed read is not an empty roadmap, so the rows already on screen are left as they are
        rather than being cleared — the projects are still the ones the user selected.
      */
      /*
        Superseded: a later read now owns the roadmap, so this one neither reports nor writes. It also
        leaves the loading state alone — the read that replaced it is still running behind it.
      */
      if (!isCurrent()) return;

      // Raised here rather than where the read happens, so only the read that owns the roadmap warns.
      if (milestones.notice) Toast.warn({ message: MILESTONE_NOTICE_MESSAGE[milestones.notice] });

      if (res.status !== Status.Success || res.data?.results === undefined) {
        Toast.error({ message: "Unable to load the roadmap." });
        setIsLoading(false);
        return;
      }

      setData(
        res.data.results.map((project) => {
          const parsedJsonDates = JSON.parse(project.dates, jsonReviver) as ProjectDates;

          return {
            uuid: project.uuid,
            displayName: project.displayName,
            rowVersion: project.rowVersion,
            currentStatus: project.status,
            cachedPhase: parseProjectPhase(project.cachedPhase) ?? ProjectPhase.feasibility,
            milestones: milestones.byProject.get(project.uuid) ?? [],
            dates: {
              ...parsedJsonDates,
              ongoingMonitoring: {
                ...parsedJsonDates.ongoingMonitoring,
                cycles: parsedJsonDates.ongoingMonitoring?.cycles?.sort((a, b) => a.sequence - b.sequence) ?? [],
              },
            } as ProjectDates,
            standardSpecificSchemaUuid: project.standardSpecificSchemaUuid,
          };
        })
      );

      if (res.data.results.some((project) => !uuids.includes(project.uuid))) {
        setRoadmapProjectUuids(() => res.data?.results.map((project) => project.uuid) ?? []);

        updateProjectRoadmapLocalStorage();
      }

      setIsLoading(false);
    },
    [fetchMilestones, roadmapProjectUuids]
  );

  /* ── Milestone task overlay ─────────────────────────────────────────────── */

  const [selectedTask, setSelectedTask] = useState<WorkflowTaskPanelNode>();
  const [showTaskPanel, setShowTaskPanel] = useState(false);
  const [taskPanelRefresh, setTaskPanelRefresh] = useState(false);

  const onTaskWorkflowError = useCallback((): void => {
    Toast.error({ message: "Couldn't load the task's workflow - please try again later." });
  }, []);

  // A general task has no workflow, so nothing is read for one and the overlay opens without a board.
  const taskPanelWorkflow = useTaskPanelWorkflow(selectedTask?.workflowUuid, onTaskWorkflowError, taskPanelRefresh);

  /*
    Resolves `?taskUuid=` — set by a clicked milestone, or by a notification linking straight to this
    page. The hook takes the param back out of the URL once it has acted on it, so clicking the same
    diamond again re-opens the overlay instead of navigating to an identical URL with nothing to run.
  */
  const deepLinkedTask = useDeepLinkedTask();
  useEffect(() => {
    if (!deepLinkedTask) return;

    setSelectedTask(deepLinkedTask);
    setShowTaskPanel(true);
  }, [deepLinkedTask]);

  /*
    A saved task reloads both sides: the overlay's own data, and the roadmap — a moved deadline, or a
    task that is no longer a milestone, moves or drops its diamond.
  */
  const handleTaskPanelRefresh = useCallback((): void => {
    setTaskPanelRefresh((prev) => !prev);

    if (roadmapProjectUuids.length) fetchRoadmapData(roadmapProjectUuids, { quiet: true });
  }, [fetchRoadmapData, roadmapProjectUuids]);

  const closeTaskPanel = useCallback((): void => {
    setSelectedTask(undefined);
    setShowTaskPanel(false);
  }, []);

  const onSaveProjectsSelectData = (): void => {
    setErrors([]);
    if (roadmapProjectUuids && roadmapProjectUuids?.length > MAX_PROJECTS) {
      setErrors([{ code: "ERROR_15_PROJECTS_MAXIMUM", message: "Please select no more than 15 projects" }]);
      return;
    }

    updateProjectRoadmapLocalStorage();
    if (roadmapProjectUuids) fetchRoadmapData(roadmapProjectUuids);

    setShowAddProjectsModal(false);
  };

  // Get project uuids from local storage if they exist
  const fetchAndSetRoadmapProjectUuids = (): void => {
    const localStorageProjectsString = safeLocalStorage.getItem(projectRoadmapLocalStorageItemName);
    if (localStorageProjectsString) {
      const localStorageProjectsObject = JSON.parse(localStorageProjectsString);
      setRoadmapProjectUuids(localStorageProjectsObject);
      if (localStorageProjectsObject) {
        fetchRoadmapData(localStorageProjectsObject);
      }
    } else {
      setIsLoading(false);
    }
  };

  const closeSelectProjectsModal = (): void => {
    setShowAddProjectsModal(false);
    fetchAndSetRoadmapProjectUuids();
  };

  useDeepCompareEffectNoCheck(() => {
    const projectUuid = searchParams.get("projectUuid");
    if (projectUuid) {
      setEditDatesPanelData(data.filter((project) => project.uuid === projectUuid)[0]);
      setShowEditDatesPanel(true);
    }
  }, [searchParams, data]);

  useDeepCompareEffectNoCheck(() => {
    if (editDatesPanelData) {
      setData((projectRoadmapData) => {
        return projectRoadmapData.map((project) => {
          if (project.uuid === editDatesPanelData.uuid && project.rowVersion !== editDatesPanelData.rowVersion) {
            return {
              ...project,
              dates: editDatesPanelData.dates,
              milestones: editDatesPanelData.milestones,
              rowVersion: editDatesPanelData.rowVersion,
            };
          }
          return project;
        });
      });
    }
  }, [editDatesPanelData]);

  useEffect(() => {
    fetchAndSetRoadmapProjectUuids();
    fetchSelectData();
  }, []);

  return {
    errors,
    data,
    ganttRows,
    theme,
    onRowClick,
    onPhaseClick,
    onMilestoneClick,
    onKeyDateClick,
    projectsMultiSelectData,

    editDatesPanelData,
    editDatesPanelPhase,
    setEditDatesPanelData,
    showEditDatesPanel,
    closeEditDatesPanel,

    selectedTask,
    taskPanelWorkflow,
    showTaskPanel,
    taskPanelRefresh,
    handleTaskPanelRefresh,
    closeTaskPanel,

    roadmapProjectUuids,
    setRoadmapProjectUuids,

    showAddProjectsModal,
    setShowAddProjectsModal,
    closeSelectProjectsModal,

    isLoading,
    setIsLoading,

    onSaveProjectsSelectData,
    fetchSelectData,
  };
};
